import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import {
  pageTitleClass,
  pageSubtitleClass,
  cardClass,
  cardBodyClass,
  cardHeaderClass,
  cardTitleClass,
  badgeClass,
  formLabelClass,
  formControlClass,
  requiredMarkClass,
  btnPrimaryClass,
  btnSecondaryClass,
  btnDangerClass,
  alertErrorClass,
  alertInfoClass,
  spinnerClass,
  tableClass,
  tableHeadClass,
  tableHeaderCellClass,
  tableBodyClass,
  tableRowClass,
  tableCellClass,
  tableActionsCellClass,
} from "../../dashboardStyles";
import {
  GetAdminInvitesAction,
  CreateAdminInviteAction,
  ResendAdminInviteAction,
  RevokeAdminInviteAction,
  GetAdminsAction,
} from "../../../../store/action";
import { notify } from "../../../../utils/notify";

const STATUS_BADGE = {
  pending: "bg-amber-50 text-amber-700",
  accepted: "bg-brand-50 text-brand-700",
  revoked: "bg-ink-100 text-ink-500",
  expired: "bg-ink-100 text-ink-500",
};

/**
 * The two actions below only make sense while nobody has redeemed the link.
 * An accepted invitation has become an account, and taking that away is a
 * different job from withdrawing an offer.
 */
const isOpen = (invite) => invite.status !== "accepted";

/**
 * Administrators, and the invitations that create them.
 *
 * This is the only route into the `Admin` role. Public signup refuses it
 * outright, and nothing promotes an existing account — so the only way to get
 * a colleague helping with the platform is to invite them from here.
 *
 * SystemOwner-only, and that is the point rather than a detail: an admin who
 * could invite peers could widen who runs the platform without anyone above
 * them deciding to. `constants/navigation.js` keeps the tab and its URL away
 * from every other role; `AdminInviteController` and `AdminInviteServices`
 * both refuse them again server-side.
 *
 * Unlike a company invitation, the link is emailed rather than copied — it is
 * bound to one address, hashed, and single use, so there is nothing to show
 * again afterwards. A link that went astray is replaced with Resend, which
 * kills the old one.
 */
const Admins = () => {
  const { t, i18n } = useTranslation();

  const [invites, setInvites] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const [inviteRows, adminRows] = await Promise.all([
        GetAdminInvitesAction(),
        GetAdminsAction(),
      ]);
      setInvites(Array.isArray(inviteRows) ? inviteRows : []);
      setAdmins(Array.isArray(adminRows) ? adminRows : []);
    } catch (err) {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /** The server's own reason when it gave one, else a generic per-action message. */
  const messageFor = (err, action) => {
    const code = err?.response?.data?.code;
    return code
      ? t(`Dashboard.Platform.admins.errors.${code}`, {
          defaultValue: t(`Dashboard.Platform.admins.errors.${action}`),
        })
      : t(`Dashboard.Platform.admins.errors.${action}`);
  };

  // The API says exactly which way a request failed (`code` on the error body),
  // so the page can name the problem instead of shrugging. A coded refusal is a
  // rule (already invited, already registered): yellow, do something else. No
  // code — a network drop, a 500 — or a mail that would not send is red.
  const reportFailure = (err, action) => {
    const code = err?.response?.data?.code;
    const message = messageFor(err, action);
    if (code && code !== "EmailFailed") notify.warning(message);
    else notify.error(message);
  };

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const invited = email.trim();
      await CreateAdminInviteAction({
        email: invited,
        note: note.trim() || null,
      });
      setEmail("");
      setNote("");
      notify.success(t("Dashboard.Platform.admins.sent", { email: invited }));
      await load();
    } catch (err) {
      reportFailure(err, "create");
    } finally {
      setCreating(false);
    }
  };

  const resend = async (invite) => {
    setBusyId(invite.id);
    try {
      await ResendAdminInviteAction(invite.id);
      notify.success(
        t("Dashboard.Platform.admins.sent", { email: invite.invitedEmail }),
      );
      await load();
    } catch (err) {
      reportFailure(err, "resend");
    } finally {
      setBusyId(null);
    }
  };

  const revoke = async (invite) => {
    setBusyId(invite.id);
    try {
      await RevokeAdminInviteAction(invite.id);
      notify.success(t("Dashboard.Platform.admins.revoked"));
      await load();
    } catch (err) {
      reportFailure(err, "revoke");
    } finally {
      setBusyId(null);
    }
  };

  const formatDate = (value) =>
    value ? new Date(value).toLocaleDateString(i18n.language) : "—";

  return (
    <div>
      <div className="mb-6">
        <h1 className={pageTitleClass}>
          {t("Dashboard.Platform.admins.title")}
        </h1>
        <p className={pageSubtitleClass}>
          {t("Dashboard.Platform.admins.subtitle")}
        </p>
      </div>

      {failed && (
        <div className={alertErrorClass} role="alert">
          <i className="bi bi-exclamation-triangle" aria-hidden="true" />
          <span>{t("Dashboard.Platform.admins.loadError")}</span>
        </div>
      )}

      <div className={`${cardClass} mb-6`}>
        <form
          onSubmit={create}
          className="admin-invite-form flex flex-col gap-3 p-4 md:flex-row md:items-end"
        >
          <div className="min-w-0 flex-1">
            <label className={formLabelClass} htmlFor="admin-invite-email">
              {t("Dashboard.Platform.admins.form.email")}
              <span className={requiredMarkClass}> *</span>
            </label>
            <input
              id="admin-invite-email"
              type="email"
              required
              className={formControlClass}
              value={email}
              placeholder={t("Dashboard.Platform.admins.form.emailPlaceholder")}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className="min-w-0 flex-1">
            <label className={formLabelClass} htmlFor="admin-invite-note">
              {t("Dashboard.Platform.admins.form.note")}
            </label>
            <input
              id="admin-invite-note"
              type="text"
              className={formControlClass}
              value={note}
              placeholder={t("Dashboard.Platform.admins.form.notePlaceholder")}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>

          <button type="submit" className={btnPrimaryClass} disabled={creating}>
            <i className="bi bi-envelope-plus" aria-hidden="true" />
            {t("Dashboard.Platform.admins.form.submit")}
          </button>
        </form>
      </div>

      {/* Says what accepting actually grants, because "admin" is the one word
          on this page that means something different to everyone reading it. */}
      <div className={`${alertInfoClass} mb-6`}>
        <i className="bi bi-info-circle" aria-hidden="true" />
        <span>{t("Dashboard.Platform.admins.explainer")}</span>
      </div>

      {loading ? (
        <div className="flex min-h-[30vh] items-center justify-center">
          <div
            role="status"
            aria-label={t("Dashboard.Menu.loading")}
            className={spinnerClass}
          />
        </div>
      ) : (
        <>
          {invites.length === 0 ? (
            <div className={alertInfoClass}>
              <i className="bi bi-envelope" aria-hidden="true" />
              <span>{t("Dashboard.Platform.admins.empty")}</span>
            </div>
          ) : (
            <div className={`${cardClass} admin-invite-list`}>
              <div className={cardBodyClass}>
                <table className={tableClass}>
                  <thead className={tableHeadClass}>
                    <tr>
                      <th className={tableHeaderCellClass}>
                        {t("Dashboard.Platform.admins.columns.invited")}
                      </th>
                      <th className={tableHeaderCellClass}>
                        {t("Dashboard.Platform.admins.columns.status")}
                      </th>
                      <th className={tableHeaderCellClass}>
                        {t("Dashboard.Platform.admins.columns.invitedBy")}
                      </th>
                      <th className={tableHeaderCellClass}>
                        {t("Dashboard.Platform.admins.columns.expires")}
                      </th>
                      <th className={tableHeaderCellClass}>
                        <span className="sr-only">
                          {t("Dashboard.Platform.admins.columns.actions")}
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className={tableBodyClass}>
                    {invites.map((invite) => (
                      <tr key={invite.id} className={tableRowClass}>
                        <td
                          className={tableCellClass}
                          data-label={t(
                            "Dashboard.Platform.admins.columns.invited",
                          )}
                        >
                          <span className="flex flex-col">
                            <span className="font-medium text-ink-900">
                              {invite.invitedEmail}
                            </span>
                            {invite.note && (
                              <span className="text-xs text-ink-500">
                                {invite.note}
                              </span>
                            )}
                          </span>
                        </td>
                        <td
                          className={tableCellClass}
                          data-label={t(
                            "Dashboard.Platform.admins.columns.status",
                          )}
                        >
                          <span
                            className={`${badgeClass} ${STATUS_BADGE[invite.status] ?? "bg-ink-100 text-ink-500"}`}
                          >
                            {t(
                              `Dashboard.Platform.admins.status.${invite.status}`,
                            )}
                          </span>
                        </td>
                        <td
                          className={tableCellClass}
                          data-label={t(
                            "Dashboard.Platform.admins.columns.invitedBy",
                          )}
                        >
                          {invite.invitedByName || "—"}
                        </td>
                        <td
                          className={tableCellClass}
                          data-label={t(
                            "Dashboard.Platform.admins.columns.expires",
                          )}
                        >
                          {/* Once redeemed, the expiry date is meaningless —
                              what matters is when they joined. */}
                          {invite.status === "accepted"
                            ? t("Dashboard.Platform.admins.joinedOn", {
                                date: formatDate(invite.acceptedAt),
                              })
                            : formatDate(invite.expiresAt)}
                        </td>
                        <td className={tableActionsCellClass}>
                          <span className="flex flex-wrap justify-end gap-2">
                            {isOpen(invite) && (
                              <>
                                <button
                                  type="button"
                                  className={btnSecondaryClass}
                                  disabled={busyId === invite.id}
                                  onClick={() => resend(invite)}
                                >
                                  <i
                                    className="bi bi-arrow-repeat"
                                    aria-hidden="true"
                                  />
                                  {t("Dashboard.Platform.admins.resend")}
                                </button>
                                {invite.status === "pending" && (
                                  <button
                                    type="button"
                                    className={btnDangerClass}
                                    disabled={busyId === invite.id}
                                    onClick={() => revoke(invite)}
                                  >
                                    {t("Dashboard.Platform.admins.revoke")}
                                  </button>
                                )}
                              </>
                            )}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Who actually holds the role right now. The invitations above are
              only how people got here — this is the answer to "who are my
              admins", which is otherwise not visible anywhere. */}
          <div className={`${cardClass} admin-list mt-6`}>
            <div className={cardHeaderClass}>
              <h2 className={cardTitleClass}>
                {t("Dashboard.Platform.admins.currentTitle")}
              </h2>
            </div>
            <div className={cardBodyClass}>
              {admins.length === 0 ? (
                <p className="m-0 text-sm text-ink-500">
                  {t("Dashboard.Platform.admins.noneYet")}
                </p>
              ) : (
                <ul className="m-0 flex list-none flex-col gap-3 p-0">
                  {admins.map((admin) => (
                    <li
                      key={admin.id}
                      className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5"
                    >
                      <span className="font-medium text-ink-900">
                        {admin.firstName} {admin.lastName}
                      </span>
                      <span className="text-sm text-ink-500">
                        {admin.email}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Admins;
